import { createSlice } from "@reduxjs/toolkit";

import { carriedBelongsToPage } from "./pageIdentity";
import {
  fetchVisiConversations,
  fetchVisiEnabledPages,
  fetchVisiHistory,
  openVisiConversation,
} from "./readThunks";
import { staleField } from "./requestToken";

export const VisiPanelView = Object.freeze({
  CHAT: "chat",
  CONVERSATIONS: "conversations",
});

export const INITIAL_STATE = {
  page: null,
  pageOwner: null,
  isOpen: false,
  turns: [],
  progress: null,
  summary: null,
  isLoadingSummary: false,
  isSuggestingQuestions: false,
  hasSummaryError: false,
  hasSkippedSummary: false,
  summaryErrorMessage: null,
  suggestionErrorMessage: null,
  isLoadingHistory: false,
  hasResolvedHistory: false,
  canRevealCitations: false,
  view: VisiPanelView.CHAT,
  conversationId: null,
  conversationTitle: null,
  conversationPageId: null,
  conversationPageTitle: null,
  conversationPageParams: {},
  isNamingConversation: false,
  startNewConversation: false,
  conversations: [],
  isLoadingConversations: false,
  latestConversationsRequestId: null,
  isListingAllPages: false,
  areListedConversationsFromAllPages: false,
  abandonedConversationEpoch: 0,
  pendingClear: null,
  conversationMutationEpoch: 0,
  conversationMutations: {},
  pageGeneration: 0,
  contextGeneration: 0,
  enabledPages: [],
  hasResolvedEnabledPages: false,
  carriedConversation: null,
};

const LENIENT_PAGE_KEY = { pageKey: "lenient" };

const RESTORED_TURN_ID_PREFIX = "restored-";

const restoredTurn = (turn, index) => ({
  id: `${RESTORED_TURN_ID_PREFIX}${index}`,
  question: turn?.question || "",
  answer: turn?.answer || "",
  isStreaming: false,
  error: null,
  // The same shape the live citations event delivers, so a restored answer renders and reveals its
  // sources through exactly the path a fresh one does.
  citations: turn?.citations?.citationChunks ?? [],
  quotes: turn?.citations?.quotes ?? {},
});

export const SUGGESTION_ERROR_MESSAGE =
  "Visi could not suggest more questions just now. Please try again.";

const withoutDuplicates = (questions, taken) =>
  (questions || []).reduce((kept, question) => {
    const trimmed = question?.trim();
    if (trimmed && !taken.has(trimmed)) {
      taken.add(trimmed);
      kept.push(trimmed);
    }
    return kept;
  }, []);

const recordMutation = (state, conversationId) => {
  state.conversationMutationEpoch += 1;
  if (conversationId != null) {
    state.conversationMutations[conversationId] =
      (state.conversationMutations[conversationId] ?? 0) + 1;
  }
};

// The page fields go with the conversation: they are what tells the panel it belongs somewhere
// else, and left behind they strand it on the banner for a chat that is gone. pendingClear is not
// touched — a clear running against some other conversation is none of this one's business.
const forgetOpenConversation = (state) => {
  state.abandonedConversationEpoch += 1;
  state.turns = [];
  state.progress = null;
  state.conversationId = null;
  state.conversationTitle = null;
  state.conversationPageId = null;
  state.conversationPageTitle = null;
  state.conversationPageParams = {};
  state.isNamingConversation = false;
  state.hasResolvedHistory = true;
  state.startNewConversation = true;
  state.view = VisiPanelView.CHAT;
};

const lastTurn = (state) => state.turns[state.turns.length - 1];

const isSupersededConversationsRequest = (state, { requestId }) =>
  state.latestConversationsRequestId != null &&
  state.latestConversationsRequestId !== requestId;

// A turn's callbacks can outlive it: the user can stop it, ask something else, or open another
// conversation while the stream is still arriving. Without the id, late chunks land on whichever
// turn happens to be last.
const addressedTurn = (state, turnId) => {
  const turn = lastTurn(state);
  if (turnId == null) {
    return turn;
  }
  return turn?.id === turnId ? turn : undefined;
};

const visiEverywhereSlice = createSlice({
  name: "everywhere",
  initialState: INITIAL_STATE,
  reducers: {
    reset() {
      return { ...INITIAL_STATE };
    },
    conversationCarried(state, { payload }) {
      state.carriedConversation = payload?.conversationId
        ? {
            conversationId: payload.conversationId,
            pageId: payload.pageId ?? null,
            pageParams: payload.pageParams ?? {},
          }
        : null;
    },
    registerPage(state, { payload }) {
      state.pageOwner = payload?.pageId ? (payload.owner ?? null) : null;
      state.page = payload?.pageId
        ? {
            pageId: payload.pageId,
            params: payload.params || {},
            state: payload.state || {},
            ...(payload.identityParams != null
              ? { identityParams: payload.identityParams }
              : {}),
          }
        : null;
      state.isLoadingHistory = false;
      state.hasResolvedHistory = false;
      state.isLoadingSummary = false;
      state.isSuggestingQuestions = false;
      state.hasSummaryError = false;
      state.hasSkippedSummary = false;
      state.summaryErrorMessage = null;
      state.suggestionErrorMessage = null;
      state.summary = null;
      state.turns = [];
      state.progress = null;
      state.view = VisiPanelView.CHAT;
      state.conversationId = null;
      state.conversationTitle = null;
      state.conversationPageId = null;
      state.conversationPageTitle = null;
      state.conversationPageParams = {};
      state.startNewConversation = false;
      state.conversations = [];
      state.isNamingConversation = false;
      state.pageGeneration += 1;
      state.contextGeneration += 1;
      if (!carriedBelongsToPage(state.carriedConversation, state.page)) {
        state.carriedConversation = null;
      }
    },
    setCitationReveal(state, { payload }) {
      if (payload?.owner !== state.pageOwner) {
        return;
      }
      state.canRevealCitations = Boolean(payload?.canReveal);
    },
    setPageContext(state, { payload }) {
      if (payload?.owner !== state.pageOwner || !state.page) {
        return;
      }
      state.page.params = payload.params || {};
      state.page.state = payload.state || {};
      state.contextGeneration += 1;
    },
    unregisterPage(state, { payload }) {
      if (payload?.owner !== state.pageOwner) {
        return;
      }
      state.pageOwner = null;
      state.page = null;
      state.canRevealCitations = false;
      state.isOpen = false;
      state.turns = [];
      state.summary = null;
      state.hasSummaryError = false;
      state.hasSkippedSummary = false;
      state.summaryErrorMessage = null;
      state.suggestionErrorMessage = null;
      state.progress = null;
      state.isLoadingHistory = false;
      state.hasResolvedHistory = false;
      state.isLoadingSummary = false;
      state.isSuggestingQuestions = false;
      state.view = VisiPanelView.CHAT;
      state.conversationId = null;
      state.conversationTitle = null;
      state.conversationPageId = null;
      state.conversationPageTitle = null;
      state.conversationPageParams = {};
      state.startNewConversation = false;
      state.conversations = [];
      state.isNamingConversation = false;
      state.pageGeneration += 1;
      state.contextGeneration += 1;
    },
    openPanel(state) {
      state.isOpen = true;
    },
    closePanel(state) {
      state.isOpen = false;
    },
    startTurn(state, { payload }) {
      if (state.startNewConversation) {
        state.abandonedConversationEpoch += 1;
      }
      state.startNewConversation = false;
      state.turns.push({
        id: payload.id,
        question: payload.question,
        answer: "",
        isStreaming: true,
        error: null,
      });
      state.progress = null;
    },
    appendAnswer(state, { payload }) {
      const turn = addressedTurn(state, payload?.turnId);
      if (turn?.isStreaming) {
        turn.answer += payload.chunk;
        state.progress = null;
      }
    },
    retractAnswer(state, { payload }) {
      const turn = addressedTurn(state, payload?.turnId);
      if (turn?.isStreaming) {
        turn.answer = "";
      }
    },
    setProgress(state, { payload }) {
      if (!addressedTurn(state, payload?.turnId)?.isStreaming) {
        return;
      }
      state.progress = payload?.message || null;
    },
    failTurn(state, { payload }) {
      const turn = addressedTurn(state, payload?.turnId);
      if (!turn) {
        return;
      }
      turn.isStreaming = false;
      turn.quotes = turn.quotes ?? {};
      turn.error = payload?.message ?? null;
      turn.errorCode = payload?.code ?? null;
      state.progress = null;
    },
    // Not guarded on isStreaming: the citations event can land after the stream has closed.
    citationsReceived(state, { payload }) {
      const turn = addressedTurn(state, payload?.turnId);
      if (turn) {
        turn.citations = payload?.citationChunks ?? [];
        turn.quotes = payload?.quotes ?? {};
      }
    },
    finishTurn(state, { payload }) {
      const turn = addressedTurn(state, payload?.turnId);
      if (!turn) {
        return;
      }
      turn.isStreaming = false;
      turn.quotes = turn.quotes ?? {};
      state.progress = null;
    },
    clearRequested(state, { payload }) {
      state.pendingClear = {
        conversationId: payload?.conversationId ?? null,
        requestId: payload?.requestId ?? null,
      };
    },
    // Settling is keyed on the request rather than on what is selected now, because the panel can
    // have moved on by the time a clear comes back and it must not stay shut for a request that
    // has already finished.
    clearSettled(state, { payload }) {
      if (state.pendingClear?.requestId === payload?.requestId) {
        state.pendingClear = null;
      }
    },
    // Clear Chat empties the conversation and keeps it, so the panel goes on pointing at the same
    // id and title. What is emptied is the chat the confirmation named, which is not always the one
    // on screen by the time the server answers.
    clearConversation(state, { payload }) {
      if (state.pendingClear?.requestId === payload?.requestId) {
        state.pendingClear = null;
      }
      const target = payload?.conversationId ?? state.conversationId;
      recordMutation(state, target);
      const cleared = state.conversations.find(
        (conversation) => conversation.id === target
      );
      if (cleared) {
        cleared.turnCount = 0;
        cleared.firstPrompt = null;
      }
      if (target != null && state.conversationId !== target) {
        return;
      }
      // An answer already in flight belongs to the messages that were just discarded, not to the
      // chat that survived them.
      state.abandonedConversationEpoch += 1;
      state.turns = [];
      state.progress = null;
      state.hasResolvedHistory = true;
      state.startNewConversation = false;
      state.view = VisiPanelView.CHAT;
      state.isNamingConversation = false;
    },
    conversationStarted(state) {
      state.abandonedConversationEpoch += 1;
      state.turns = [];
      state.progress = null;
      state.hasResolvedHistory = true;
      state.conversationId = null;
      state.conversationTitle = null;
      state.conversationPageId = null;
      state.conversationPageParams = {};
      state.startNewConversation = true;
      state.view = VisiPanelView.CHAT;
      state.isNamingConversation = false;
    },
    conversationRenamed(state, { payload }) {
      const renamed = state.conversations.find(
        (conversation) => conversation.id === payload?.id
      );
      if (renamed) {
        renamed.title = payload.title;
      }
      if (state.conversationId === payload?.id) {
        state.conversationTitle = payload.title;
      }
    },
    conversationRemoved(state, { payload }) {
      state.conversations = state.conversations.filter(
        (conversation) => conversation.id !== payload?.id
      );
      recordMutation(state, payload?.id);
    },
    // The optimistic removal already moved the token, so a read started while the DELETE was in
    // flight carries the new one and would be accepted. Confirmation moves it again.
    conversationDeleted(state, { payload }) {
      recordMutation(state, payload?.id);
      state.conversations = state.conversations.filter(
        (conversation) => conversation.id !== payload?.id
      );
      if (state.conversationId !== payload?.id) {
        return;
      }
      forgetOpenConversation(state);
    },
    setPanelView(state, { payload }) {
      state.view = payload;
    },
    conversationNaming(state) {
      state.isNamingConversation = true;
    },
    conversationNamingStopped(state) {
      state.isNamingConversation = false;
    },
    summaryRequested(state) {
      state.isLoadingSummary = true;
      state.hasSummaryError = false;
      state.summaryErrorMessage = null;
    },
    questionsRequested(state) {
      state.isSuggestingQuestions = true;
      state.hasSummaryError = false;
      state.summaryErrorMessage = null;
      state.suggestionErrorMessage = null;
    },
    summaryReceived(state, { payload }) {
      const stale = staleField(state, payload, LENIENT_PAGE_KEY);
      if (stale === "pageKey") {
        return;
      }
      if (stale) {
        state.isLoadingSummary = false;
        return;
      }
      const followUpQuestions = withoutDuplicates(
        payload?.followUpQuestions,
        new Set()
      );
      state.isLoadingSummary = false;
      state.hasSkippedSummary = false;
      state.suggestionErrorMessage = null;
      state.summary = {
        summaryParagraphs: payload?.summaryParagraphs || [],
        followUpQuestions,
        writtenAt: payload?.writtenAt ?? null,
        // Where the rule goes. A regenerated summary is a new set of originals, so this moves
        // with it rather than stranding the rule above questions that are no longer on screen.
        originalQuestionCount: followUpQuestions.length,
      };
    },
    summarySkipped(state, { payload }) {
      const stale = staleField(state, payload, LENIENT_PAGE_KEY);
      if (stale === "pageKey") {
        return;
      }
      if (stale) {
        state.isLoadingSummary = false;
        return;
      }
      state.isLoadingSummary = false;
      state.hasSummaryError = false;
      state.hasSkippedSummary = true;
      state.summaryErrorMessage = null;
    },
    summaryFailed(state, { payload }) {
      const stale = staleField(state, payload, LENIENT_PAGE_KEY);
      if (stale === "pageKey") {
        return;
      }
      if (stale) {
        state.isLoadingSummary = false;
        return;
      }
      state.isSuggestingQuestions = false;
      state.isLoadingSummary = false;
      state.hasSummaryError = true;
      state.summaryErrorMessage = payload?.userMessage ?? null;
    },
    questionsAdded(state, { payload }) {
      if (staleField(state, payload, LENIENT_PAGE_KEY)) {
        return;
      }
      state.isSuggestingQuestions = false;
      state.isLoadingSummary = false;
      if (!state.summary) {
        return;
      }
      const fresh = withoutDuplicates(
        payload?.followUpQuestions,
        new Set(state.summary.followUpQuestions)
      );
      // Nothing new to show is the same outcome for the reader as the request failing, and it
      // must not leave a rule with nothing under it.
      if (fresh.length === 0) {
        state.suggestionErrorMessage = SUGGESTION_ERROR_MESSAGE;
        return;
      }
      state.summary.followUpQuestions.push(...fresh);
      state.suggestionErrorMessage = null;
    },
    questionsFailed(state, { payload }) {
      if (staleField(state, payload, LENIENT_PAGE_KEY)) {
        return;
      }
      state.isSuggestingQuestions = false;
      state.suggestionErrorMessage =
        payload?.userMessage || SUGGESTION_ERROR_MESSAGE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVisiEnabledPages.fulfilled, (state, { payload }) => {
        state.enabledPages = payload || [];
        state.hasResolvedEnabledPages = true;
      })
      .addCase(fetchVisiEnabledPages.rejected, (state) => {
        state.enabledPages = [];
        state.hasResolvedEnabledPages = false;
      })
      .addCase(fetchVisiHistory.pending, (state) => {
        state.isLoadingHistory = true;
      })
      .addCase(fetchVisiHistory.fulfilled, (state, { payload }) => {
        const stale = staleField(state, payload);
        if (stale === "pageKey") {
          return;
        }
        // A clear that finished while this was in flight emptied the very conversation it is
        // carrying. Left unresolved rather than resolved, so the panel asks again for fresh history
        // instead of settling on messages that are gone.
        if (stale) {
          state.isLoadingHistory = false;
          return;
        }
        // New Chat is a deliberate move away from whatever the server still calls current, so a
        // history response that was already in flight must not pull the old conversation back.
        if (state.startNewConversation) {
          state.isLoadingHistory = false;
          return;
        }
        const isPanelUntouched =
          !state.hasResolvedHistory && state.turns.length === 0;
        state.isLoadingHistory = false;
        state.hasResolvedHistory = true;
        state.conversationId = payload.conversationId ?? null;
        state.conversationTitle = payload.title ?? null;
        state.conversationPageId = payload.pageId ?? null;
        state.conversationPageTitle = payload.pageTitle ?? null;
        state.conversationPageParams = payload.pageParams ?? {};
        if (state.conversationTitle) {
          state.isNamingConversation = false;
        }
        if (isPanelUntouched) {
          state.turns = (payload.turns || []).map(restoredTurn);
        }
      })
      .addCase(fetchVisiHistory.rejected, (state, { payload }) => {
        if (!payload) {
          state.isLoadingHistory = false;
          return;
        }
        const wasSupersededByANewerRead = staleField(state, {
          ...payload,
          epoch: undefined,
        });
        if (wasSupersededByANewerRead) {
          return;
        }
        state.isLoadingHistory = false;
        state.hasResolvedHistory = true;
      })
      .addCase(fetchVisiConversations.pending, (state, { meta }) => {
        state.isLoadingConversations = true;
        state.latestConversationsRequestId = meta.requestId;
        state.isListingAllPages = Boolean(meta.arg);
      })
      .addCase(fetchVisiConversations.fulfilled, (state, { meta, payload }) => {
        if (isSupersededConversationsRequest(state, meta)) {
          return;
        }
        state.isLoadingConversations = false;
        const wasListedBeforeTheChatsChanged = staleField(
          state,
          payload,
          LENIENT_PAGE_KEY
        );
        if (wasListedBeforeTheChatsChanged) {
          return;
        }
        state.conversations = payload?.conversations || [];
        state.isListingAllPages = Boolean(meta.arg);
        state.areListedConversationsFromAllPages = Boolean(meta.arg);
      })
      .addCase(fetchVisiConversations.rejected, (state, { meta }) => {
        if (isSupersededConversationsRequest(state, meta)) {
          return;
        }
        state.isLoadingConversations = false;
      })
      .addCase(openVisiConversation.fulfilled, (state, { payload }) => {
        const belongsToAPageOrChatTheUserHasLeft = staleField(
          state,
          payload,
          LENIENT_PAGE_KEY
        );
        if (belongsToAPageOrChatTheUserHasLeft) {
          return;
        }
        state.abandonedConversationEpoch += 1;
        if (
          state.carriedConversation?.conversationId === payload?.conversationId
        ) {
          state.carriedConversation = null;
        }
        state.turns = (payload?.turns || []).map(restoredTurn);
        state.conversationId = payload?.conversationId ?? null;
        state.conversationTitle = payload?.title ?? null;
        state.conversationPageId = payload?.pageId ?? null;
        state.conversationPageTitle = payload?.pageTitle ?? null;
        state.conversationPageParams = payload?.pageParams ?? {};
        state.startNewConversation = false;
        state.isNamingConversation = false;
        state.progress = null;
        state.hasResolvedHistory = true;
        state.view = VisiPanelView.CHAT;
      })
      .addCase(openVisiConversation.rejected, (state, { payload }) => {
        if (!payload || payload.pageGeneration !== state.pageGeneration) {
          return;
        }
        state.carriedConversation = null;
      });
  },
});

export const {
  reset,
  registerPage,
  setCitationReveal,
  setPageContext,
  unregisterPage,
  openPanel,
  closePanel,
  startTurn,
  appendAnswer,
  citationsReceived,
  retractAnswer,
  setProgress,
  failTurn,
  finishTurn,
  clearConversation,
  clearRequested,
  clearSettled,
  conversationCarried,
  conversationDeleted,
  conversationRemoved,
  conversationRenamed,
  conversationStarted,
  conversationNaming,
  conversationNamingStopped,
  setPanelView,
  summaryRequested,
  summaryReceived,
  summaryFailed,
  summarySkipped,
  questionsAdded,
  questionsFailed,
  questionsRequested,
} = visiEverywhereSlice.actions;

export default visiEverywhereSlice.reducer;
